import React, { Fragment, useState, useCallback, useMemo, useRef } from "react";
import { Dropdown, DropdownItemProps, DropdownProps, Button, Input, Segment, InputProps, Select, Header, SelectProps, Form, CheckboxProps } from "semantic-ui-react";
import { useObservationFilters } from "../../store/selectors/observationListSelectors";
import { SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import { EObservationStatus } from "../../interfaces/IObservation";
import { useTranslation } from "react-i18next";
import { useLoggedInUser } from "../../auth/store/authSelectors";
import _debounce from 'lodash/debounce';
import { IObservationFilters } from "../../store/reducers/observationListReducer";
import { useCategoryList } from "../../store/selectors/categorySelectors";
import { isLoaded } from "../../../../sg-core";
import { useApplicationConfiguration } from "../../configuration/ConfigurationContext";

const makeLabel = (color: SemanticCOLORS) => ({
    color,
    empty: true,
    circular: true,
});

const makeOption = (text: string, color: SemanticCOLORS, value?: EObservationStatus): DropdownItemProps => ({
    key: value ?? "undefined",
    value,
    text,
    label: makeLabel(color),
});

export const useStatusFilterOptions = () => {
    const { t } = useTranslation();
    const texts = {
        new: t("Uusi"),
        inProgess: t("Käsittelyssä"),
        finished: t("Käsitelty"),
        all: t("Kaikki")
    }
    return useMemo((): DropdownItemProps[] => [
        makeOption(texts.new, "red", EObservationStatus.New),
        makeOption(texts.inProgess, "yellow", EObservationStatus.InProgress),
        makeOption(texts.finished, "green", EObservationStatus.Finished),
        //  makeOption(t("Myöhässä"), "black", EObservationStatus.Late),
        makeOption(texts.all, "grey", EObservationStatus.Unknown),
    ], [t]);
};

export const useCategoryOptions = () => {
    const categories = useCategoryList();

    if (isLoaded(categories)) {
        return categories.data.map((category) => ({
            key: category.id,
            text: category.name,
            value: category.id,
            icon: category.icon
        }));
    }

    return []

}



export const WorkListFilters: React.FC = () => {
    const { t } = useTranslation();
    const user = useLoggedInUser();

    const { components: { LocationFilter }, archiveLimit, minimumSearchQueryLength } = useApplicationConfiguration()
    const { filters, update } = useObservationFilters();
    const [freeTextResetKey, setFreeTextResetKey] = useState(1);

    const statusFilterChange: DropdownProps["onChange"] = useCallback((_1, { value }) => {
        const isAllSelected = value === EObservationStatus.Unknown;
        const status = value && !isAllSelected ? value : undefined;
        update({ status });
    }, [update]);

    const creatorFilterChange: DropdownProps["onChange"] = useCallback((_1, { value }) => {
        const creator = value === "own" ? user?.hprPersonId : undefined
        const handler = value === "responsible" ? user?.hprPersonId : undefined
        update({ creator, handler });
    }, [update, user]);


    const categoryFilterChange: SelectProps["onChange"] = useCallback((_1, { value }) => {
        update({ category: value });
    }, [update]);



    const includeArchiveFilterChange: CheckboxProps["onChange"] = (_1, { value, checked }) => {
        update({ includeArchived: typeof checked === "boolean" ? checked : false });
    };

    const debouncedUpdate = useRef(_debounce((value: Partial<IObservationFilters>) => update(value), 400)).current;

    const textFilterChange: InputProps["onChange"] = (_1, { value }) => {
        const queryLength = value.length;
        const prevQueryLength = filters.freeText?.length || 0;
        if (queryLength >= minimumSearchQueryLength || queryLength < prevQueryLength) {
            debouncedUpdate({ freeText: value || undefined });
        };
    };



    const clearTextFilter = () => {
        update({ freeText: undefined });
        setFreeTextResetKey((key) => key + 1);
    }

    const [searchVisible, setSearchVisible] = useState(false);
    const [extendedSearchVisible, setExtendedSearchVisible] = useState(false);



    const statusOptions = useStatusFilterOptions();
    const categoryOptions = useCategoryOptions()
    const currentWhose = user && filters.creator === user.hprPersonId ? "own" : user && filters.handler === user.hprPersonId ? "responsible" : "all";

    return (
        <Fragment>

            <Dropdown
                options={statusOptions}
                selection
                button
                placeholder={t("Tila")}
                onChange={statusFilterChange}
                value={filters.status || EObservationStatus.Unknown}
            />

            <Dropdown
                button
                placeholder={t("Tekijä")}
                selection
                options={[
                    { key: 1, text: t("Omat"), value: "own" },
                    { key: 2, text: t("Vastuulla"), value: "responsible" },
                    { key: 3, text: t("Kaikkien"), value: "all" }
                ]}
                onChange={creatorFilterChange}
                value={currentWhose}
            />

            <Button onClick={() => setExtendedSearchVisible(!extendedSearchVisible)} icon="ellipsis horizontal" />

            <Button onClick={() => setSearchVisible(!searchVisible)} icon="search" />

            <div className={`sg-reveal${extendedSearchVisible ? " sg-active" : ""}`}>
                <span />
                <Segment >
                    <Header.Subheader as="h4" >{t("Kategoria")}</Header.Subheader>
                    <Select
                        fluid
                        search
                        placeholder={t("Kategoria")}
                        options={categoryOptions}
                        onChange={categoryFilterChange}
                        value={filters.category}
                        clearable
                    />



                    {archiveLimit && (
                        <Fragment>
                            <br />
                            <Form.Checkbox
                                label={t("Näytä arkistoidut")}
                                onChange={includeArchiveFilterChange}
                                checked={filters.includeArchived}
                            />
                        </Fragment>
                    )}

                    {LocationFilter ? (
                        <Fragment>

                            <Header.Subheader as="h4" >{t("Sijainti")}</Header.Subheader>
                            <Form as="div">
                                <Form.Group inline widths="4" >
                                    <LocationFilter
                                        filters={filters}
                                        onChange={update}
                                        fluid
                                    />
                                </Form.Group>
                            </Form>
                        </Fragment>
                    ) : null}


                </Segment>
                <span />
            </div>



            <div className={`sg-reveal${searchVisible ? " sg-active" : ""}`}>
                <span />
                <Segment >
                    <Input key={freeTextResetKey} onChange={textFilterChange} fluid icon="search" type="search" placeholder={`${t("Hae havainnoista")}...`} />
                </Segment>
                <span />
            </div>

            {filters.freeText ? (
                <div style={{ padding: "10px 0" }}>
                    {t("Haetaan tekstillä")}: <strong> {filters.freeText}</strong>
                    <a style={{ marginLeft: "10px", cursor: "pointer" }} onClick={clearTextFilter}> {t("Tyhjennä")} </a>
                </div>
            ) : null}

        </Fragment>
    );
}



